/**
 * Storefront catalog sort options (PD-15272).
 *
 * One list shared by the three places that have to agree on it:
 *  - the storefront catalog's Sort dropdown (which options exist, their
 *    labels, and the `sortBy` / `sortOrder` URL params each one writes),
 *  - the Portal Site editor's per-brand "Default sort" picker, and
 *  - crm-api, which validates a brand's default sort and drops it for a
 *    visitor whose storefront does not offer that sort (price sorts when
 *    pricing is hidden, or anything the tenant's `catalog.sortOptions`
 *    preference leaves out).
 *
 * Keeping them on one table is what stops a default saved in the editor from
 * naming a sort the storefront cannot apply.
 */

export const STOREFRONT_CATALOG_SORT_VALUES = [
	'relevance',
	'popularity',
	'recently_added',
	'newest',
	'name_asc',
	'name_desc',
	'price_asc',
	'price_desc',
] as const;

export type StorefrontCatalogSortValue =
	(typeof STOREFRONT_CATALOG_SORT_VALUES)[number];

export interface StorefrontCatalogSortOptionModel {
	/** Stable value stored as a brand's default sort and used by the dropdown. */
	value: StorefrontCatalogSortValue;
	/** Source label (the UI wraps it with t()). */
	label: string;
	/**
	 * The tenant `catalog.sortOptions` key that switches this option on. Name
	 * and Price each expand into an ascending and a descending option. Null for
	 * Relevance, which the storefront always offers.
	 */
	preferenceKey: string | null;
	/** `sortBy` URL param the option writes. */
	sortBy: string;
	/** `sortOrder` URL param the option writes; null writes none. */
	sortOrder: 'ASC' | 'DESC' | null;
}

/**
 * Every sort the storefront can show, in the order the dropdown lists the
 * options that share a preference key. `recently_added` writes the internal
 * `createdDate`, which the faceted catalog maps to `externalCreatedDate`; the
 * mapping lives in the storefront so older deep links keep working.
 */
export const STOREFRONT_CATALOG_SORT_OPTIONS: ReadonlyArray<StorefrontCatalogSortOptionModel> =
	[
		{
			value: 'relevance',
			label: 'Relevance',
			preferenceKey: null,
			sortBy: 'relevance',
			sortOrder: null,
		},
		{
			value: 'popularity',
			label: 'Most Popular',
			preferenceKey: 'popularity',
			sortBy: 'uniqueClientsAggregate',
			sortOrder: 'DESC',
		},
		{
			value: 'recently_added',
			label: 'Recently Added',
			preferenceKey: 'recently_added',
			sortBy: 'createdDate',
			sortOrder: 'DESC',
		},
		{
			value: 'newest',
			label: 'Newest Arrivals',
			preferenceKey: 'newest',
			sortBy: 'externalCreatedDate',
			sortOrder: 'DESC',
		},
		{
			value: 'name_asc',
			label: 'Name: A to Z',
			preferenceKey: 'name',
			sortBy: 'description',
			sortOrder: 'ASC',
		},
		{
			value: 'name_desc',
			label: 'Name: Z to A',
			preferenceKey: 'name',
			sortBy: 'description',
			sortOrder: 'DESC',
		},
		{
			value: 'price_asc',
			label: 'Price: Low to High',
			preferenceKey: 'price',
			sortBy: 'price',
			sortOrder: 'ASC',
		},
		{
			value: 'price_desc',
			label: 'Price: High to Low',
			preferenceKey: 'price',
			sortBy: 'price',
			sortOrder: 'DESC',
		},
	];

/** Narrow an unknown persisted value (hand-edited JSON included) to a sort value. */
export function isStorefrontCatalogSortValue(
	value: unknown,
): value is StorefrontCatalogSortValue {
	return (
		typeof value === 'string' &&
		(STOREFRONT_CATALOG_SORT_VALUES as ReadonlyArray<string>).includes(value)
	);
}

/** The option for a sort value, or undefined for an unknown value. */
export function findStorefrontCatalogSortOption(
	value: unknown,
): StorefrontCatalogSortOptionModel | undefined {
	return STOREFRONT_CATALOG_SORT_OPTIONS.find(
		(option) => option.value === value,
	);
}

/**
 * The dropdown options a storefront shows for the tenant's enabled
 * `catalog.sortOptions` keys: Relevance first (always offered), then each
 * enabled key's options in the order the keys are listed, so the tenant's
 * ordering of discovery sorts ahead of name and price is kept.
 */
export function resolveStorefrontCatalogSortOptions(
	preferenceKeys: ReadonlyArray<string>,
): Array<StorefrontCatalogSortOptionModel> {
	const resolved: Array<StorefrontCatalogSortOptionModel> =
		STOREFRONT_CATALOG_SORT_OPTIONS.filter(
			(option) => option.preferenceKey == null,
		);

	for (const key of preferenceKeys) {
		for (const option of STOREFRONT_CATALOG_SORT_OPTIONS) {
			if (option.preferenceKey === key && !resolved.includes(option)) {
				resolved.push(option);
			}
		}
	}

	return resolved;
}

/**
 * The sort value the storefront's URL params describe, or null when they
 * describe none (the standard order). The inverse of an option's
 * `sortBy` / `sortOrder`: Name and Price read as ascending unless the order
 * is explicitly DESC, matching how the dropdown has always read the URL.
 */
export function storefrontCatalogSortValueFromParams(
	sortBy: string | null,
	sortOrder: string | null,
): StorefrontCatalogSortValue | null {
	switch (sortBy) {
		case 'relevance':
			return 'relevance';
		case 'uniqueClientsAggregate':
			return 'popularity';
		case 'createdDate':
			return 'recently_added';
		case 'externalCreatedDate':
			return 'newest';
		case 'description':
			return sortOrder === 'DESC' ? 'name_desc' : 'name_asc';
		case 'price':
			return sortOrder === 'DESC' ? 'price_desc' : 'price_asc';
		default:
			return null;
	}
}
